import {
  Transportation,
  CreateTransportationRequest,
  UpdateTransportationRequest,
  TransportationResponse,
  TransportationTypeInfo,
  DakoTestConnectionResult,
  DakoDiscoverRequest,
  DakoDiscoveryResult,
  ApiResponse
} from '../types/transportation';
import { getToken } from '../utils/auth';

// Resolve API base: use absolute VITE_API_BASE when provided; otherwise current origin.
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

class TransportationApiService {
  private async fetchPublic(url: string, options: RequestInit = {}): Promise<Response> {
    const token = getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}/api${url}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.error('Unauthorized! Token may be invalid or expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  }

  async getAllTransportations(includeInactive: boolean = false): Promise<Transportation[]> {
    try {
      const url = `/Transportation${includeInactive ? '?includeInactive=true' : ''}`;
      const response = await this.fetchPublic(url);
      const result: ApiResponse<TransportationResponse[]> = await response.json();

      if (result.success && result.data) {
        return result.data.map(this.mapResponseToTransportation);
      }

      throw new Error(result.message || 'Failed to fetch transportations');
    } catch (error) {
      console.error('Error fetching transportations:', error);
      throw error;
    }
  }

  async getTransportationById(id: number): Promise<Transportation> {
    try {
      const response = await this.fetchPublic(`/Transportation/${id}`);
      const result: ApiResponse<TransportationResponse> = await response.json();

      if (result.success && result.data) {
        return this.mapResponseToTransportation(result.data);
      }

      throw new Error(result.message || 'Failed to fetch transportation');
    } catch (error) {
      console.error('Error fetching transportation:', error);
      throw error;
    }
  }

  async createTransportation(request: CreateTransportationRequest): Promise<Transportation> {
    try {
      const response = await this.fetchPublic(`/Transportation`, {
        method: 'POST',
        body: JSON.stringify(request),
      });

      const result: ApiResponse<TransportationResponse> = await response.json();

      if (result.success && result.data) {
        return this.mapResponseToTransportation(result.data);
      }

      throw new Error(result.message || 'Failed to create transportation');
    } catch (error) {
      console.error('Error creating transportation:', error);
      throw error;
    }
  }

  async updateTransportation(id: number, request: UpdateTransportationRequest): Promise<Transportation> {
    try {
      const response = await this.fetchPublic(`/Transportation/${id}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });

      const result: ApiResponse<TransportationResponse> = await response.json();

      if (result.success && result.data) {
        return this.mapResponseToTransportation(result.data);
      }

      throw new Error(result.message || 'Failed to update transportation');
    } catch (error) {
      console.error('Error updating transportation:', error);
      throw error;
    }
  }

  async deleteTransportation(id: number): Promise<void> {
    try {
      const response = await this.fetchPublic(`/Transportation/${id}`, {
        method: 'DELETE',
      });

      const result: ApiResponse<any> = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete transportation');
      }
    } catch (error) {
      console.error('Error deleting transportation:', error);
      throw error;
    }
  }

  async toggleTransportationActive(id: number): Promise<void> {
    try {
      const response = await this.fetchPublic(`/Transportation/${id}/toggle-active`, {
        method: 'PATCH',
      });

      const result: ApiResponse<any> = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to toggle transportation status');
      }
    } catch (error) {
      console.error('Error toggling transportation status:', error);
      throw error;
    }
  }

  async canDeleteTransportation(id: number): Promise<boolean> {
    try {
      const response = await this.fetchPublic(`/Transportation/${id}/canDelete`);
      const result: ApiResponse<boolean> = await response.json();

      if (result.success) {
        return result.data || false;
      }

      throw new Error(result.message || 'Failed to check if transportation can be deleted');
    } catch (error) {
      console.error('Error checking if transportation can be deleted:', error);
      throw error;
    }
  }

  /** Test the DAKO connection for a stored transport method using its saved credentials. */
  async testDakoConnection(transportationId: number): Promise<DakoTestConnectionResult> {
    const response = await this.fetchPublic(`/tracking/dako/transportations/${transportationId}/test`, {
      method: 'POST',
    });
    return response.json();
  }

  /**
   * Discover the companies/vehicles available to the given DAKO credentials.
   * Uses the credentials from the request directly, so it works before saving.
   */
  async discoverDakoVehicles(req: DakoDiscoverRequest): Promise<DakoDiscoveryResult> {
    const response = await this.fetchPublic(`/tracking/dako/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    return response.json();
  }

  async getTransportationTypes(): Promise<TransportationTypeInfo[]> {
    try {
      const response = await this.fetchPublic(`/Transportation/types`);
      const result: ApiResponse<TransportationTypeInfo[]> = await response.json();

      if (result.success && result.data) {
        return result.data;
      }

      throw new Error(result.message || 'Failed to fetch transportation types');
    } catch (error) {
      console.error('Error fetching transportation types:', error);
      throw error;
    }
  }

  private mapResponseToTransportation(response: TransportationResponse): Transportation {
    return {
      id: response.id,
      name: response.name,
      type: response.type,
      description: response.description,
      website: response.website,
      contactEmail: response.contactEmail,
      contactPhone: response.contactPhone,
      address: response.address,
      logo: response.logo,
      costPerKm: response.costPerKm,
      capacity: response.capacity,
      features: response.features,
      isActive: response.isActive,
      createdAt: response.createdAt,
      dakoCompanyIdent: response.dakoCompanyIdent,
      dakoVehicleIdent: response.dakoVehicleIdent,
      dakoUsername: response.dakoUsername,
      dakoPollIntervalSeconds: response.dakoPollIntervalSeconds,
      dakoApiBaseUrl: response.dakoApiBaseUrl,
      hasDakoConfig: response.hasDakoConfig,
    };
  }
}

export const transportationApi = new TransportationApiService();
